import type { LithologyType } from '@/api/endpoints';

// Fuzzy-dobór litologii dla warstw z AI: najpierw trafienie w słowniku (po symbolu/nazwie),
// potem fallback rodziny litologii (wzór/kolor po słowie kluczowym). Zwraca kolor/wzór/typ.

export interface LithoMatch {
  color: string;
  pattern: string;
  lithologyTypeId: number | null;
  code: string | null;
  name: string | null;
}

// UWAGA: NFD nie rozkłada „ł" — trzeba je zamienić jawnie na „l".
const norm = (s: string | null | undefined): string =>
  (s ?? '').toLowerCase().replace(/ł/g, 'l').normalize('NFD').replace(/\p{Diacritic}/gu, '').trim();

// Słowa barwy/konsystencji w nazwach słownika, które NIE powinny decydować o dopasowaniu.
const STOPWORDS = new Set([
  'zolty', 'szary', 'brazowy', 'brunatny', 'jasny', 'ciemny', 'zielony', 'oliwkowy',
  'rdzawy', 'bialy', 'czarny', 'plastyczny', 'jasnoszary', 'ciemnoszary', 'szarozielony',
]);

// Kolejność: od najbardziej specyficznych do ogólnych (np. „piaskowiec" przed „piasek", „glina" przed „ił").
const KEYWORD_FALLBACK: Array<{ re: RegExp; pattern: string; color: string }> = [
  { re: /piaskowiec/, pattern: 'VERTICAL_LINES', color: '#b89860' },
  { re: /wapie[nń]|wapien/, pattern: 'BRICKS', color: '#d4d4d4' },
  { re: /margiel|marglist/, pattern: 'BRICKS_MARL', color: '#9aa8a0' },
  { re: /dolomit/, pattern: 'BRICKS_DIAGONAL', color: '#cfd6dd' },
  { re: /lupek|ilowiec/, pattern: 'WAVES', color: '#6a6a72' },
  { re: /pospol|otoczak|glaz|zlepieniec|kamien/, pattern: 'PEBBLES', color: '#c4a85a' },
  { re: /zwir/, pattern: 'CIRCLES', color: '#b89860' },
  { re: /glin|zwalow|morena/, pattern: 'HORIZONTAL_LINES', color: '#8b6850' },
  { re: /piasek|piask|piaszczyst|pospolk/, pattern: 'DOTS', color: '#e0d29a' },
  { re: /mulek|mulow|\bmul\b|pyl/, pattern: 'DASHES', color: '#b7a98a' },
  { re: /(^|[^a-z])il/, pattern: 'DIAGONAL_LINES', color: '#6a6b58' },
  { re: /wegiel/, pattern: 'SOLID', color: '#3a3128' },
  { re: /torf|humus|gleba/, pattern: 'CROSS', color: '#5a4a35' },
];

export function matchLithology(
  symbol: string | null | undefined,
  description: string | null | undefined,
  lithos: LithologyType[],
): LithoMatch | null {
  const asMatch = (t: LithologyType): LithoMatch =>
    ({ color: t.color, pattern: t.pattern, lithologyTypeId: t.id, code: t.code, name: t.name });

  // 1) symbol → kod słownika (np. „gz" → „Gz")
  const sym = norm(symbol);
  if (sym) {
    const byCode = lithos.find((t) => norm(t.code) === sym);
    if (byCode) return asMatch(byCode);
  }

  // 2) punktacja po słowach nazwy litologii występujących w opisie
  const desc = norm(description);
  if (desc) {
    let best: LithologyType | null = null, bestScore = 0;
    for (const t of lithos) {
      const words = norm(t.name).split(/[^a-z0-9]+/).filter((w) => w.length >= 3 && !STOPWORDS.has(w));
      let score = 0;
      for (const w of words) if (desc.includes(w)) score += w.length;
      if (score > bestScore) { bestScore = score; best = t; }
    }
    if (best && bestScore >= 4) return asMatch(best);
  }

  // 3) fallback rodziny litologii (sam wzór/kolor, bez typu ze słownika)
  const hay = desc || sym;
  for (const k of KEYWORD_FALLBACK) {
    if (k.re.test(hay)) return { color: k.color, pattern: k.pattern, lithologyTypeId: null, code: null, name: null };
  }
  return null;
}
